import React from 'react'

import clsx from 'clsx'
import { useRouter } from 'next/router'

import QueryLoader from '~/components/QueryLoader'
import { StatsCard, StatsCardInfoTooltip } from '~/components/stats/StatsComponents'
import { useLpiSummary } from '~/components/stats/useLpiSummary'
import SkeletonLoader from '~/components/ui/SkeletonLoader'
import useTrackClick from '~/events/trackers/useTrackClick'
import { useCommonTx } from '~/hooks/useTranslationForNamespace'
import Clock from '~/images/icons/Raw/Clock.svg'
import ArrowRight from '~/images/icons/SystemOutlined/ArrowRight.svg'
import ChevronRight from '~/images/icons/SystemOutlined/ChevronRight.svg'
import ThunderIcon from '~/images/stats/Thunder.svg'
import { ProgressCompletionBar } from '~/ui/Progress/ProgressBar'

/**
 *
 * for now all layouts are mostly same, except few changes.
 * there might be drastic change is these layouts in future,
 * the core ui rendering will mostly be same
 */
export const LpiStatCard = ({ layoutName }: { layoutName: 'A' | 'B' | 'C' }) => {
  const { loading, error, hasAreaWithNoLpi, lpiByArea, overallLpi, overallLpiText, overallPercent } = useLpiSummary()

  const { trackCta } = useTrackClick()
  const router = useRouter()
  const t = useCommonTx('stats')
  const ct = useCommonTx('common')

  const onListClick = ({ areaSlug }: { areaSlug: string }) => {
    trackCta({
      type: 'button',
      click_name: 'stats_lpi_card_area_click',
      destination: 'game_lpi_rankings',
      text: ct(`areas.${areaSlug}.name`),
      value: areaSlug,
    })
    router.push(`/training-areas?area_slug=${areaSlug}`)
  }

  const onOverallLpiClick = () => {
    router.push(`/training-areas`)
  }

  const isLayoutA = layoutName === 'A'

  return (
    <QueryLoader
      loading={loading}
      error={error}
      data={!loading && lpiByArea}
      LoadingComponent={<SkeletonLoader className='rounded-lg min-h-[300px] w-full' />}
    >
      <StatsCard
        title={
          <div className='flex flex-row items-center gap-2'>
            <ThunderIcon title='t' className='w-4 h-6' />
            {isLayoutA ? ct('lpiText') : ct('currentLpi')}
          </div>
        }
        titleAction={isLayoutA ? <WhatIsLpi /> : undefined}
        footerAction={
          isLayoutA ? null : (
            <button
              onClick={() => router.push(`/stats`)}
              className='flex flex-row items-center gap-1 text-icon-subdued text-caption-1'
            >
              <span>{ct('moreInStats')}</span>
              <ArrowRight title='t' className='w-5 h-5' />
            </button>
          )
        }
      >
        <OverallLpiSection
          className='py-5 pl-6 pr-5'
          progress={overallPercent}
          progressValue={overallLpiText}
          title={t('card.lpi.overallTitle')}
          emptyText={
            hasAreaWithNoLpi
              ? t('card.lpi.hasAreaWithNoLpiText')
              : overallLpi <= 0
              ? t('card.lpi.initialText')
              : undefined
          }
          onClick={isLayoutA ? () => onOverallLpiClick() : undefined}
        />

        <div className='pl-12 pb-2 flex flex-col'>
          {lpiByArea.map((item) => {
            return (
              <AreaItem
                key={item.areaSlug}
                className={'border-t border-border-subdued pr-5 py-3'}
                title={ct(`areas.${item.areaSlug}.name`)}
                titleInfo={item.notIncludedInLPI ? t('notIncludedInLPI') : undefined}
                progress={item.progress}
                progressValue={
                  item.lpi === 0 ? <div className='text-text-disabled'>--</div> : <div>{item.lpiText}</div>
                }
                onClick={isLayoutA ? () => onListClick({ areaSlug: item.areaSlug }) : undefined}
              />
            )
          })}
        </div>
      </StatsCard>
    </QueryLoader>
  )
}

const OverallLpiSection = ({
  title,
  emptyText,
  progress,
  progressValue,
  className,
  onClick,
}: {
  title: React.ReactNode
  emptyText?: React.ReactNode
  progress: number
  progressValue: React.ReactNode
  className?: string
  onClick?: () => void
}) => {
  return (
    <button
      className={clsx(
        'flex flex-row justify-center items-center gap-4 font-normal w-full',
        !onClick && 'cursor-default',
        className,
      )}
      onClick={() => onClick?.()}
    >
      <div className='flex flex-col justify-center w-full font-normal flex-1'>
        <div className='flex flex-row justify-between mb-1 text-body-text-2 font-semibold'>{title}</div>
        {emptyText && <div className='text-caption-1 text-text-subdued text-left'>{emptyText}</div>}
        {!emptyText && (
          <div className='flex flex-row items-center gap-2 font-normal text-heading-3'>
            <ProgressCompletionBar progress={progress} height={24} />
            {progressValue}
          </div>
        )}
      </div>
      {onClick && <ChevronRight title='t' className='block min-w-7 min-h-7 text-icon-disabled' />}
    </button>
  )
}

const AreaItem = ({
  title,
  titleInfo,
  progress,
  progressValue,
  className,
  onClick,
}: {
  progress: number
  title: React.ReactNode
  titleInfo?: React.ReactNode
  progressValue: React.ReactNode
  className?: string
  onClick?: () => void
}) => {
  return (
    <button
      className={clsx(
        'flex flex-row justify-center items-center gap-4 font-normal',
        !onClick && 'cursor-default',
        className,
      )}
      onClick={() => onClick?.()}
    >
      <div className='flex flex-col w-full'>
        <div className='flex flex-row justify-between items-center mb-1 font-normal text-body-text-3'>
          <div className='flex flex-row items-baseline flex-wrap gap-1'>
            {title} {titleInfo && <div className='text-caption-2 text-text-subdued'>{titleInfo}</div>}
          </div>
        </div>
        <div className='flex flex-row items-center gap-2 font-normal text-body-text-3'>
          <ProgressCompletionBar progress={progress} height={18} />
          {progressValue}
        </div>
      </div>
      {onClick && <ChevronRight title='t' className='block min-w-7 min-h-7 text-icon-disabled' />}
    </button>
  )
}

const WhatIsLpi = () => {
  const t = useCommonTx('stats.card.lpi')
  const { trackCta } = useTrackClick()

  const onShowCallback = () => {
    trackCta({
      click_name: 'stats_lpi_card_tooltip',
      type: 'button',
      text: '',
    })
  }

  const tooltip = (
    <div className='flex flex-col gap-6 text-body-text-3'>
      <p>{t('description.p')}</p>
      <p>{t('description.p2')}</p>
    </div>
  )

  const tooltipFooter = (
    <div className='flex flex-col gap-2 text-body-text-3'>
      <p className='flex flex-row items-center gap-3 font-semibold'>
        <Clock title='t' className='w-5 h-5 text-text-interactive' />
        {t('comingSoon.title')}
      </p>
      <p>{t('comingSoon.l1')}</p>
    </div>
  )

  return (
    <StatsCardInfoTooltip
      title={t('description.title')}
      tooltip={tooltip}
      tooltipFooter={tooltipFooter}
      onShowCallback={onShowCallback}
    />
  )
}
